import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './BrowseInitiatives.css';
import '../styles/ResizableTable.css';
import { initiativeService, Initiative } from '../services/api';
import { api } from '../config';
import Spinner from './Spinner';
import { initResizableTables } from '../utils/resizableTables';

const BrowseInitiatives: React.FC = () => {
  const [initiatives, setInitiatives] = useState<Initiative[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedInitiative, setSelectedInitiative] = useState<Initiative | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [engagementSummary, setEngagementSummary] = useState<Record<string, { like_count: number; partner_count: number }>>({});
  const itemsPerPage = 5;

  useEffect(() => {
    const fetchInitiatives = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await initiativeService.getInitiatives();
        // Sort by submitted_date (newest first)
        const allInitiatives = response.data
          .sort((a: Initiative, b: Initiative) => new Date(b.submitted_date).getTime() - new Date(a.submitted_date).getTime());
        setInitiatives(allInitiatives);
        if (allInitiatives.length > 0) {
          try {
            const ids = allInitiatives.map((i: Initiative) => i.id).join(',');
            const res = await axios.get(api(`/api/initiatives/engagements/summary?initiative_ids=${ids}`));
            setEngagementSummary(res.data);
          } catch { /* silent */ }
        }
      } catch (err: any) {
        setError('Failed to load initiatives');
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitiatives();
  }, []);

  // Initialize resizable tables when initiatives load
  useEffect(() => {
    if (!isLoading && initiatives.length > 0) {
      const timer = setTimeout(() => {
        initResizableTables();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isLoading, initiatives.length]);

  // Search and filter
  const filteredInitiatives = initiatives.filter((initiative) => {
    const searchLower = searchQuery.toLowerCase();
    return (
      (initiative.use_case_name?.toLowerCase().includes(searchLower) || '') ||
      (initiative.purpose?.toLowerCase().includes(searchLower) || '') ||
      (initiative.proposed_solution_owner?.toLowerCase().includes(searchLower) || '')
    );
  });

  // Pagination
  const totalPages = Math.ceil(filteredInitiatives.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const displayedInitiatives = filteredInitiatives.slice(startIndex, startIndex + itemsPerPage);

  const handlePreviousPage = () => {
    setCurrentPage((prev) => Math.max(1, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(totalPages, prev + 1));
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <div className="browse-initiatives-container">
        <Spinner message="Loading approved initiatives..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="browse-initiatives-container">
        <div style={{ textAlign: 'center', padding: '40px', color: '#d32f2f' }}>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="browse-initiatives-container">
      <div className="browse-initiatives-header">
        <p className="initiatives-count">Showing all initiatives ({initiatives.length})</p>
      </div>

      {initiatives.length === 0 ? (
        <div className="no-initiatives-message">
          <p>No approved initiatives yet.</p>
        </div>
      ) : (
        <>
          <div className="browse-search-section">
            <input
              type="text"
              placeholder="Search by title, description, or division..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              className="search-input"
            />
          </div>

          {displayedInitiatives.length === 0 ? (
            <div className="no-results-message">No initiatives match your search.</div>
          ) : (
            <>
              <div className="browse-table-wrapper">
                <table id="browse-initiatives-table" className="initiatives-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Use Case</th>
                      <th>Purpose</th>
                      <th>Owner</th>
                      <th>Submitted By</th>
                      <th>Date</th>
                      <th style={{textAlign:'center'}}>Status</th>
                      <th style={{textAlign:'center'}}>Action</th>
                      <th style={{textAlign:'center'}}>Engagement</th>
                      <th style={{width:'24px', minWidth:'24px', maxWidth:'24px', padding:0}}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayedInitiatives.map((initiative, idx) => (
                      <tr key={initiative.id}>
                        <td>{startIndex + idx + 1}</td>
                        <td className="col-usecase">{initiative.use_case_name}</td>
                        <td className="col-purpose">{initiative.purpose?.substring(0, 100)}{initiative.purpose && initiative.purpose.length > 100 ? '...' : ''}</td>
                        <td>{initiative.proposed_solution_owner || 'N/A'}</td>
                        <td>{initiative.submitted_by || 'N/A'}</td>
                        <td className="col-date">{formatDate(initiative.submitted_date || '')}</td>
                        <td style={{textAlign:'center'}}>
                          <span className={`status-badge ${(initiative.status || 'pending').toLowerCase()}`}>
                            {initiative.status === 'approved' ? 'Approved' : initiative.status === 'rejected' ? 'Rejected' : initiative.status === 'parked' ? 'Parked' : 'Pending'}
                          </span>
                        </td>
                        <td style={{textAlign:'center'}}><button className="view-details-btn" onClick={() => setSelectedInitiative(initiative)}>Details</button></td>
                        <td style={{textAlign:'center', fontSize:12}}>
                          {(() => {
                            const eng = engagementSummary[String(initiative.id)];
                            if (!eng || (eng.like_count === 0 && eng.partner_count === 0)) return <span style={{color:'#adb5bd'}}>—</span>;
                            return (
                              <span style={{display:'flex',flexDirection:'column',gap:2,alignItems:'center'}}>
                                {eng.like_count > 0 && <span style={{color:'#6941c6'}}>👍 {eng.like_count}</span>}
                                {eng.partner_count > 0 && <span style={{color:'#17b26a'}}>🤝 {eng.partner_count}</span>}
                              </span>
                            );
                          })()}
                        </td>
                        <td style={{width:'24px', minWidth:'24px', maxWidth:'24px', padding:0}}></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="browse-pagination">
                  <button
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                    className="pagination-btn"
                  >
                    Previous
                  </button>
                  <span className="page-info">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    className="pagination-btn"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {selectedInitiative && (
        <div className="modal-overlay" onClick={() => setSelectedInitiative(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{selectedInitiative.use_case_name}</h3>
              <button
                className="close-btn"
                onClick={() => setSelectedInitiative(null)}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="detail-row">
                <strong>Status:</strong>
                <span className="status-badge approved">Approved</span>
              </div>
              <div className="detail-row">
                <strong>Solution Owner:</strong>
                <span>{selectedInitiative.proposed_solution_owner || 'N/A'}</span>
              </div>
              <div className="detail-row">
                <strong>Sponsor:</strong>
                <span>{selectedInitiative.solution_sponsor || 'N/A'}</span>
              </div>
              <div className="detail-row">
                <strong>Submitted by:</strong>
                <span>{selectedInitiative.submitted_by || 'N/A'}</span>
              </div>
              <div className="detail-row">
                <strong>Submitted on:</strong>
                <span>{formatDate(selectedInitiative.submitted_date || '')}</span>
              </div>
              {selectedInitiative.approval_date && (
                <div className="detail-row">
                  <strong>Approved on:</strong>
                  <span>{formatDate(selectedInitiative.approval_date)}</span>
                </div>
              )}
              <div className="detail-section">
                <strong>Purpose:</strong>
                <p>{selectedInitiative.purpose}</p>
              </div>
              {selectedInitiative.proposed_workflow && (
                <div className="detail-section">
                  <strong>Proposed Workflow:</strong>
                  <p>{selectedInitiative.proposed_workflow}</p>
                </div>
              )}
              {selectedInitiative.value && (
                <div className="detail-section">
                  <strong>Business Value:</strong>
                  <p>{selectedInitiative.value}</p>
                </div>
              )}
              {selectedInitiative.solution_scope && (
                <div className="detail-section">
                  <strong>Solution Scope:</strong>
                  <p>{selectedInitiative.solution_scope}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BrowseInitiatives;
